import { ChangeEvent, useEffect, useState } from "react";
import { useFilterContext, FilterItem } from "../../../shared/context/FilterContext";
import { Pagination } from "../../../shared/types";
import { QueryKey, useQuery } from "@tanstack/react-query";
import { portfolioApi } from "../api/portfolio";
import { queryClient } from "../../../shared/providers/QueryProvider";
import { extractFilterValues } from "../../../lib/filterUtils";

export const useFetchPortfolios = (assignedToMe: boolean) => {

    const {
        tempFilters,
        quickFilters,
        handleUpdateTempFilters,
        handleResetFilters,
        handleApplyFilters,
    } = useFilterContext();

    const [searchDraft, setSearchDraft] = useState<string>('');
    const [searchKey, setSearchKey] = useState<string>('');

    const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);

    const [page, setPage] = useState<number>(1);
    const [limit, setLimit] = useState<number>(25);

    const handleOpenFilter = () => {
        setIsOpenFilter(true);
    };

    const handleCloseFilter = () => {
        setIsOpenFilter(false);
    };

    const handleChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(e.target.value);
    };

    const handleClear = () => {
        setSearchDraft('');
        setSearchKey('');
    };

    const handleSearch = () => {
        setSearchKey(searchDraft);
    };

    const changePage = (page: number) => {
        setPage(page);
    };

    const changeRowsPerPage = (limit: number) => {
        setLimit(limit);
    };

    const [pagination, setPagination] = useState<Pagination>({
        has_next: false,
        has_previous: false,
        limit: 10,
        page: 1,
        total: 0,
        total_pages: 1,
    });

    const portfolios = useQuery({
        queryKey: [
            "portfolio-listing",
            page, limit, searchKey,assignedToMe,
            quickFilters["region"],
            quickFilters["programme"],
            quickFilters["site"],
            quickFilters["workstream"],
            quickFilters["worktype-group"],
            quickFilters["projectYear"],
            quickFilters["stakeholder"]


        ] as QueryKey,
        queryFn: () =>
            portfolioApi.fetchPortfolios({
                page, limit,
                assign_to_me: assignedToMe,
                search_term: searchKey,
                region_id: extractFilterValues(quickFilters["region"]),
                programme_id: extractFilterValues(quickFilters["programme"]),
                site_id: extractFilterValues(quickFilters["site"]),
                workstream_id: extractFilterValues(quickFilters["workstream"]),
                work_type_group_id: extractFilterValues(quickFilters["worktype-group"]),
                project_year: extractFilterValues(quickFilters["projectYear"]),
                stakeholder_id: extractFilterValues(quickFilters["stakeholder"])
            })
    });

    useEffect(() => {
        const pagination = portfolios?.data?.data?.pagination ?? {};
        setPagination({
            has_next: pagination.has_next ?? false,
            has_previous: pagination.has_previous ?? false,
            limit: pagination.limit ?? 10,
            page: pagination.page ?? 1,
            total: pagination.total ?? 0,
            total_pages: pagination.total_pages ?? 1,
        });
    }, [portfolios.data]);

    return {
        pagination,
        portfolios: portfolios?.data?.data?.rows || [],
        isLoading: portfolios.isFetching,
        isError: portfolios.isError,
        searchDraft,
        handleChangeSearch, handleClear, handleSearch, changePage, changeRowsPerPage,
        isOpenFilter, handleCloseFilter, handleOpenFilter,
        handleResetFilters, handleApplyFilters, tempFilters, handleUpdateTempFilters,
        removeCache: () => { queryClient.removeQueries({ queryKey: ["portfolio-listing"] }); }
    };

};